import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api";
import { AgentRequest, RequestStatus } from "@/lib/types";
import { getErrorMessage } from "@/lib/utils";
import { toast } from "sonner";

/** Requests (agent → lead) */
export function useRequests(status?: RequestStatus) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["requests", status ?? "all"],
    queryFn: () => apiClient.getRequests(status),
  });

  const createRequest = useMutation({
    mutationFn: (message: string) => apiClient.createRequest(message),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["requests"] });
      toast.success("Request submitted");
    },
    onError: (e: unknown) => toast.error(getErrorMessage(e, "Failed to create request")),
  });

  const updateRequest = useMutation({
    mutationFn: ({ id, message }: { id: string; message: string }) =>
      apiClient.updateRequest(id, message),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["requests"] });
      toast.success("Request updated");
    },
    onError: (e: unknown) => toast.error(getErrorMessage(e, "Failed to update request")),
  });

  const updateRequestStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: "approved" | "rejected" }) =>
      apiClient.updateRequestStatus(id, status),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["requests"] });
      toast.success(variables.status === "approved" ? "Request approved" : "Request declined");
    },
    onError: (e: unknown) =>
      toast.error(getErrorMessage(e, "Failed to update request status")),
  });

  return {
    requests: (query.data ?? []) as AgentRequest[],
    isLoading: query.isLoading,
    error: query.error,
    refetch: () => queryClient.invalidateQueries({ queryKey: ["requests"] }),
    createRequest,
    updateRequest,
    updateRequestStatus,
  };
}

export function useRequestById(id: string | undefined) {
  return useQuery({
    queryKey: ["request", id],
    queryFn: () => apiClient.getRequestById(id!),
    enabled: !!id,
  });
}
